import { match } from 'ts-pattern'

import type {
  ConditionalProperty,
  OperatorFormatter
} from 'Automations/nodeDefinitions/conditions'
import {
  phaseDuration,
  phaseName,
  readPhaseDurationValue,
  readPhaseNameValue
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings/phase'
import { semanticTypes } from 'Automations/nodeDefinitions/semanticTypes'
import {
  formatPhaseDuration,
  formatPhaseName
} from '../../../../switch/components/GroupEditor/inputs'
import { PhasePropertyEditor } from './PhasePropertyEditor'
import {
  PHASE_DURATION_OPERATOR_LABELS,
  PHASE_NAME_OPERATOR_LABELS
} from './phase.constants'
import {
  validatePhaseDurationValue,
  validatePhaseNameValue
} from './phase.validators'
import {
  getDefaultPhaseOperator,
  isPhasePropertyAvailable
} from './PhasePropertyEditor.helpers'

export const phaseConditionalProperty: ConditionalProperty = {
  label: 'Phase',
  valueSemanticType: semanticTypes.sequence.quote.phases,
  focusOperatorOnSelect: false,
  propertyAvailable: isPhasePropertyAvailable,
  getDefaultOperator: getDefaultPhaseOperator,
  propertyEditorComponent: PhasePropertyEditor,
  propertyEditorLayout: 'inline',
  validateProperty: ({ operator, value, siblingConditions }) =>
    match(operator)
      .with('NAME', () => validatePhaseNameValue({ value }))
      .with('DURATION', () =>
        validatePhaseDurationValue({ value, siblingConditions })
      )
      .otherwise(() => undefined),
  operators: {
    NAME: {
      label: 'Name',
      functionString: phaseName,
      formatPreviewOperator: formatPhaseNameOperator,
      formatPreviewValue: formatPhaseName
    },
    DURATION: {
      label: 'Duration',
      functionString: phaseDuration({
        startDateSemanticType: semanticTypes.sequence.quote.startDate
      }),
      formatPreviewOperator: formatPhaseDurationOperator,
      formatPreviewValue: formatPhaseDuration
    }
  }
}

function formatPhaseNameOperator({ value }: Parameters<OperatorFormatter>[0]) {
  const { operator } = readPhaseNameValue(value)
  if (!operator) return ''
  if (operator === 'NOT_SET') return 'name is not set'
  return `name ${PHASE_NAME_OPERATOR_LABELS[operator].toLowerCase()}`
}

function formatPhaseDurationOperator({
  value
}: Parameters<OperatorFormatter>[0]) {
  const { operator } = readPhaseDurationValue(value)
  if (!operator) return ''
  return match(operator)
    .with('IS_OPEN_ENDED', () => 'duration is open ended')
    .otherwise(
      comparison =>
        `duration ${PHASE_DURATION_OPERATOR_LABELS[comparison].toLowerCase()}`
    )
}
